"use client";
import type { SubSegment, WhisperJson } from "@teksta/shared-types";
import { useTranslations } from "next-intl";
import type { ReactNode, RefObject } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "@/lib/debounce";
import { formatTimestamp } from "@/lib/subtitles";
import { cn } from "@/lib/utils";
import { useSearchSegments } from "./hooks";
import { TranscriptionSearch } from "./SubtitleSearch";
import { segmentsBinarySearch } from "./utils";

export const SubtitleViewerLite = ({
  whisperJson,
  lunrIndex,
  videoId,
  playerRef,
  onSearch,
  searchStatus,
}: {
  onSearch?: (query: string) => void;
  searchStatus?: ReactNode;
  whisperJson: WhisperJson;
  lunrIndex: object | null | undefined;
  videoId?: string;
  playerRef?: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
}) => {
  const [_playerRef, setPlayerRef] = useState<{
    current: HTMLVideoElement | HTMLAudioElement | null;
  }>({ current: null });

  useEffect(() => {
    if (!_playerRef.current && videoId) {
      const element = document.getElementById(videoId) as HTMLVideoElement;
      setPlayerRef({ current: element });
    } else if (playerRef?.current) {
      setPlayerRef({ current: playerRef.current });
    }
  }, [videoId, playerRef]);

  useEffect(() => {
    _playerRef.current?.addEventListener("timeupdate", (e: Event) => {
      const target = e.target as HTMLMediaElement;
      const roundedTime = Math.round(target.currentTime);
      currentTimeRef.current = roundedTime;
      setCurrentTimeState(roundedTime);
    });
    return () => {
      _playerRef.current?.removeEventListener("timeupdate", () => {});
    };
  }, [_playerRef.current]);

  const currentTimeRef = useRef(0);
  const [currentTimeState, setCurrentTimeState] = useState(0);

  if (!_playerRef.current) {
    return null;
  }
  return (
    <div>
      <SegmentsViewerLite
        currentTime={currentTimeState}
        whisperJson={whisperJson}
        videoRef={_playerRef}
        lunrIndex={lunrIndex}
        onSearch={onSearch}
        searchStatus={searchStatus}
      />
    </div>
  );
};

const SegmentsViewerLite = ({
  currentTime,
  whisperJson,
  videoRef,
  lunrIndex,
  onSearch,
  searchStatus,
}: {
  onSearch?: (query: string) => void;
  searchStatus?: ReactNode;
  currentTime: number;
  whisperJson: WhisperJson;
  videoRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  lunrIndex: object | null | undefined;
}) => {
  const t = useTranslations();
  const { results, isSearched, queryTime, executeSearch, resultsIndexes, query } =
    useSearchSegments({
      lunrIndex,
      whisperJson,
    });
  const currentSegmentIndex = useMemo(() => {
    return segmentsBinarySearch({
      segments: whisperJson.segments,
      timestampS: currentTime,
    });
  }, [currentTime, whisperJson]);

  const scrollView = useRef<HTMLDivElement>(null);

  const scrollToTimestamp = ({ timestampS }: { timestampS: number }) => {
    const closestSegmentIndex = segmentsBinarySearch({
      segments: whisperJson.segments,
      timestampS,
    });
    scrollToSegment({ segmentIndex: closestSegmentIndex });
  };
  const scrollToSegment = ({ segmentIndex }: { segmentIndex: number }) => {
    const segmentElement = document.getElementById(`segment-${segmentIndex}`);
    const topPos = segmentElement?.offsetTop;
    ignoreNextScrollEvent.current = true;
    scrollView.current?.scrollTo({
      top: (topPos || 0) - 64,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (scrollView.current && currentSegmentIndex !== -1 && !isSearched) {
      scrollToSegment({ segmentIndex: currentSegmentIndex });
    }
  }, [currentSegmentIndex]);
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const handlePlay = () => {
      scrollToTimestamp({ timestampS: el.currentTime });
    };
    el.addEventListener("play", handlePlay);
    return () => {
      el.removeEventListener("play", handlePlay);
    };
  }, [videoRef, scrollView]);

  const lastKnownScrollPosition = useRef(0);
  const tickingScroll = useRef(false);
  const ignoreNextScrollEvent = useRef(true);
  const seekToScroll = (_e: Event) => {
    if (ignoreNextScrollEvent.current) {
      ignoreNextScrollEvent.current = false;
      return;
    }
  };
  useEffect(() => {
    const handleScrollEvent = (e: Event) => {
      const target = e.target as HTMLElement;
      lastKnownScrollPosition.current = target.scrollTop;
      if (!tickingScroll.current) {
        window.requestAnimationFrame(function () {
          seekToScroll(e);
          tickingScroll.current = false;
        });
        tickingScroll.current = true;
      }
    };
    const scrollEl = scrollView.current;
    if (!scrollEl) return;
    const debouncedHandler = debounce(handleScrollEvent, 200);
    scrollEl.addEventListener("scroll", debouncedHandler);
    return () => {
      scrollEl.removeEventListener("scroll", debouncedHandler);
    };
  }, [scrollView]);

  useEffect(() => {
    if (isSearched) {
      scrollView.current?.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      scrollToSegment({ segmentIndex: currentSegmentIndex });
    }
  }, [isSearched]);

  const segments = whisperJson.segments;

  // Render the whole transcript (when not searching) instead of a ±20 window, so
  // it's fully scrollable, selectable and Ctrl+F-able. content-visibility (on each
  // row) keeps off-screen rows cheap to lay out; this memo — keyed on the active
  // segment, not the raw playhead time — stops per-frame playback from reconciling
  // every row (the only playback-driven change is the current-segment highlight,
  // which moves on segment crossings, not every frame).
  const renderedRows = useMemo(() => {
    const toShow =
      resultsIndexes && resultsIndexes.length > 0
        ? resultsIndexes.map((index) => ({ segment: segments[index], index }))
        : segments.map((segment, index) => ({ segment, index }));
    return toShow.map(({ segment, index }) => {
      if (!segment) return null;
      let highlightedText = segment.text;
      if (query) {
        const queryRegex = new RegExp(query, "gi");
        highlightedText = segment.text.replace(
          queryRegex,
          (match) => `<span class="bg-success/20">${match}</span>`
        );
      }
      return (
        <div
          key={index}
          // Inline (not a Tailwind arbitrary class) so it always applies — JIT
          // generation of arbitrary `content-visibility` is unreliable per-route.
          // Skips layout/paint for off-screen rows while keeping them in the DOM.
          style={{ contentVisibility: "auto", containIntrinsicSize: "0 72px" }}
          className={cn(
            "cursor-pointer rounded bg-foreground/10 p-2 transition-colors hover:bg-foreground/15",
            {
              "bg-foreground/20 hover:bg-foreground/20": currentSegmentIndex === index,
            }
          )}
          id={`segment-${index}`}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (videoRef.current) videoRef.current.currentTime = segment.start;
          }}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && videoRef.current) {
              e.preventDefault();
              videoRef.current.currentTime = segment.start;
            }
          }}
        >
          <SubSegmentLite segment={segment} segmentText={highlightedText} />
        </div>
      );
    });
  }, [segments, resultsIndexes, query, currentSegmentIndex, videoRef]);

  return (
    <div className="space-y-2">
      {(lunrIndex || onSearch) && (
        <TranscriptionSearch
          executeSearch={({ query }) => {
            onSearch?.(query);
            executeSearch({ query });
          }}
          results={results ?? []}
          isSearched={isSearched}
        />
      )}
      {searchStatus}
      <div
        // Same fade + tail treatment as SubtitleViewer: see the note there.
        className="rounded-lg p-4 max-h-[480px] overflow-y-scroll relative sub-scrollbar [mask-image:linear-gradient(to_bottom,transparent,black_1.5rem,black_calc(100%-3rem),transparent)]"
        ref={scrollView}
      >
        <div className="space-y-2 ">{renderedRows}</div>
        <div className="h-24"></div>
      </div>
    </div>
  );
};

const SubSegmentLite = ({ segment, segmentText }: { segment: SubSegment; segmentText: string }) => {
  const segmentTimestamp = formatTimestamp({
    timestamp: segment.start * 1000,
  }).split(",")[0];

  return (
    <div className="flex justify-between space-x-2">
      <p className="wrap-break-word" dangerouslySetInnerHTML={{ __html: segmentText }}></p>
      <div className="text-foreground/80 text-xs tabular-nums">{segmentTimestamp}</div>
    </div>
  );
};
